import {useEffect, useMemo, useRef, useState} from "react";
import {apiGet} from './api';
import CrystalViewer from './crystal/CrystalViewer';
import {elementColor, FormulaText, genStructure,
        toParsedStructure} from './structure';

/** 自转迷你结构图：rAF 每帧重投影并直接写 SVG 属性（不触发 React 渲染）。
    限制 ~24fps；spin=false 时画一帧静态图。 */
function RotatingMini({struct, size = 20, spin = true}) {
  const ref = useRef(null);
  const n = (struct.species || []).length;
  const circles = useMemo(() => {
    return Array.from({length: n}, (_, i) => (
      <circle key={i} fill={elementColor(struct.species[i], i)}
              stroke="#0a0e12" strokeWidth="0.5" />
    ));
  }, [struct, size, n]);

  useEffect(() => {
    const svg = ref.current;
    if (!svg || !n) return;
    const nodes = svg.querySelectorAll('circle');
    const s = size / 2, rotX = 0.5;
    const cx = Math.cos(rotX), sx = Math.sin(rotX);
    // 每芯片不同初相位，避免整排同步转动
    const phase = (struct.positions[0] || [0])[0] * 3.7;
    const draw = (rotY) => {
      const cy = Math.cos(rotY), sy = Math.sin(rotY);
      for (let i = 0; i < n; i++) {
        const [x, y, z] = struct.positions[i];
        const x1 = x * cy + z * sy, z1 = -x * sy + z * cy;
        const y2 = y * cx - z1 * sx;
        const node = nodes[i];
        if (!node) continue;
        node.setAttribute('cx', s + x1 * s * 0.42);
        node.setAttribute('cy', s + y2 * s * 0.42);
        node.setAttribute('r', Math.max(1.6, 3.0 - z1 * 0.65));
        node.setAttribute('opacity', z1 > 0 ? 1 : 0.75);
      }
    };
    if (!spin) { draw(phase); return; }
    let raf, t0 = performance.now(), last = 0;
    const loop = (now) => {
      raf = requestAnimationFrame(loop);
      if (now - last < 42) return;   // ~24fps
      last = now;
      draw(((now - t0) / 1000) * 0.9 + phase);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [struct, size, spin, n]);

  return (
    <svg ref={ref} width={size} height={size} style={{flexShrink: 0}}>
      {circles}
    </svg>
  );
}

/* 真实结构缓存（candidateId → {species, positions}），芯片小图共用 */
const structCache = {};

/* 把真实晶体坐标归一到迷你图可视半径（居中 + max 半径 ≈ 1.9） */
function normalizeMini(struct) {
  const pos = struct && struct.positions;
  if (!pos || !pos.length) return struct;
  const n = pos.length;
  const c = [0, 1, 2].map(k => pos.reduce((a, p) => a + p[k], 0) / n);
  let r = 0;
  const centered = pos.map(p => {
    const q = p.map((v, k) => v - c[k]);
    r = Math.max(r, Math.hypot(q[0], q[1], q[2]));
    return q;
  });
  const s = r > 1e-6 ? 1.9 / r : 1;
  return {...struct, positions: centered.map(q => q.map(v => v * s))};
}

export function MoleculeChip({m, onClick}) {
  // 优先用真实输入结构（有缓存），没有 realId 或未取到时才退回按化学式生成。
  // 结构请求懒加载：芯片进入视口才拉（几百个淘汰芯片不能个个都打接口）
  const [struct, setStruct] = useState(
    () => (m.realId && structCache[m.realId]) || genStructure(m.formula));
  const [inView, setInView] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !m.realId) return;
    const io = new IntersectionObserver(
      es => { if (es.some(e => e.isIntersecting)) { setInView(true); io.disconnect(); } },
      {rootMargin: '120px'});
    io.observe(el);
    return () => io.disconnect();
  }, [m.realId]);
  useEffect(() => {
    if (!m.realId) { setStruct(normalizeMini(genStructure(m.formula))); return; }
    if (!inView) return;                       // 没进视口：先用假结构顶住
    if (structCache[m.realId]) { setStruct(structCache[m.realId]); return; }
    let alive = true;
    apiGet(`/candidates/${m.realId}/structure`)
      .then(d => {
        if (!alive || d.error) return;
        const parsed = toParsedStructure(d);
        if (parsed && parsed.atoms.length) {
          const s = normalizeMini({
            species: parsed.atoms.map(a => a.element),
            positions: parsed.atoms.map(a => [a.x, a.y, a.z]),
            lattice: parsed.lattice,
          });
          structCache[m.realId] = s;
          setStruct(s);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [m.realId, m.formula, inView]);
  // 所有状态（running/queued/done/reject…）都自转
  return (
    <div ref={rootRef}
         className={`chip ${m.lane === 'reject' ? 'chip-reject' : ''} ${m.state || ''}`}
         onClick={onClick}
         title={`${m.formula} · ${m.label}${m.state === 'queued' ? ' · 排队中' : ''}`}>
      <RotatingMini struct={struct} spin />
      <span className="chip-formula"><FormulaText f={m.formula} /></span>
    </div>
  );
}

/** 大结构弹窗：CrystalViewer（three.js，拖拽旋转/缩放）。
    realId 有值时抓取真实 extxyz 结构，否则按化学式生成假结构。 */
export function StructureModal({m, onClose}) {
  const [structure, setStructure] = useState(null);
  const [err, setErr] = useState(null);
  const [spin, setSpin] = useState(true);

  useEffect(() => {
    setStructure(null); setErr(null);
    if (m.realId) {
      apiGet(`/candidates/${m.realId}/structure`)
        .then(d => {
          if (d.error) setErr(d.error);
          else setStructure(toParsedStructure(d));
        })
        .catch(e => setErr(e.message));
    } else {
      setStructure(toParsedStructure(genStructure(m.formula)));
    }
  }, [m.realId, m.formula]);

  return (
    <>
      <div className="drawer-mask" onClick={onClose} />
      <div className="drawer struct-drawer">
        <button className="close" onClick={onClose}>×</button>
        <h2 style={{fontSize: 20}}><FormulaText f={m.formula} /></h2>
        <p className="muted">{m.label}
          {m.realId ? ' · 真实输入结构（拖拽旋转 / 滚轮缩放）'
                    : ' · 演示假结构（按化学式生成）'}</p>
        {m.tc?.tc_K != null &&
          <p style={{fontSize: 14, margin: '4px 0'}}>
            超导温度 <b style={{fontSize: 17}}>
              Tc ≈ {Number(m.tc.tc_K).toFixed(2)} K
            </b>
            <span className="muted" style={{fontSize: 12}}>
              {' '}（λ={Number(m.tc.lambda).toFixed(2)}，
              ω_log={Math.round(m.tc.omega_log_mev)} meV）
            </span>
          </p>}
        <label style={{display: 'flex', gap: 6, alignItems: 'center',
                       cursor: 'pointer', margin: '6px 0'}}>
          <input type="checkbox" checked={spin}
                 onChange={e => setSpin(e.target.checked)} />
          自转
        </label>
        {err && <p className="muted">结构加载失败：{err}</p>}
        {structure && (
          <div style={{height: 'calc(100vh - 260px)', minHeight: 420,
                       marginTop: 10}}>
            <CrystalViewer structure={structure} atomRadiusScale={0.5}
                           autoRotate={spin} />
          </div>)}
        {structure && (
          <p className="muted" style={{fontSize: 12}}>
            {Object.entries(structure.atoms.reduce((acc, a) => {
              acc[a.element] = (acc[a.element] || 0) + 1; return acc;
            }, {})).map(([el, n]) => `${el}×${n}`).join('  ')}
          </p>)}
      </div>
    </>
  );
}
